<template>
  <v-dialog :value="value" persistent scrollable max-width="760">
    <v-card>
      <v-card-title class="password-result-title px-6 pt-5">
        <v-icon color="primary" class="mr-2">mdi-email-check-outline</v-icon>
        {{ $t("Resultado_envio_novas_senhas") }}
        <v-spacer />
        <v-btn
          icon
          :disabled="processando"
          :aria-label="$t('Fechar')"
          @click="$emit('close')"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-subtitle class="px-6 pb-2">
        {{ $t("Acompanhe_envio_novas_senhas") }}
      </v-card-subtitle>

      <v-card-text class="px-6 pt-3">
        <div v-if="processando" class="password-result-progress mb-5">
          <span>
            {{
              $t("Envios_concluidos_contagem", {
                concluidos: concluidos,
                total: itens.length,
              })
            }}
          </span>
          <v-progress-linear
            :value="percentualConcluido"
            height="8"
            rounded
            color="primary"
            class="mt-2"
          />
        </div>

        <div class="password-result-stats">
          <div class="password-result-stat password-result-stat--success">
            <strong>{{ sucessos.length }}</strong>
            <span>{{ $t("Senhas_enviadas") }}</span>
          </div>
          <div class="password-result-stat password-result-stat--error">
            <strong>{{ falhas.length }}</strong>
            <span>{{ $t("Senhas_nao_enviadas") }}</span>
          </div>
          <div class="password-result-stat password-result-stat--warning">
            <strong>{{ incertos.length }}</strong>
            <span>{{ $t("Envios_sem_confirmacao") }}</span>
          </div>
        </div>

        <v-alert
          v-if="!processando && !falhas.length && !incertos.length"
          type="success"
          text
          dense
          class="mt-5 mb-0"
        >
          {{ $t("Todos_emails_nova_senha_enviados") }}
        </v-alert>

        <section v-if="falhas.length" class="password-result-section">
          <h3>{{ $t("Usuarios_com_falha_envio_senha") }}</h3>
          <p class="password-result-hint">
            {{ $t("Falhas_envio_nova_senha_ajuda") }}
          </p>
          <div class="password-result-list">
            <div
              v-for="item in falhas"
              :key="item.chave"
              class="password-result-row"
            >
              <v-icon color="error" small>mdi-alert-circle-outline</v-icon>
              <div class="password-result-row__content">
                <strong>{{ item.nome }}</strong>
                <span>{{
                  item.email || $t("Email_destino_nao_informado")
                }}</span>
                <p>{{ item.motivo }}</p>
              </div>
            </div>
          </div>
        </section>

        <section v-if="incertos.length" class="password-result-section">
          <h3>{{ $t("Usuarios_sem_confirmacao_envio_senha") }}</h3>
          <p class="password-result-hint">
            {{ $t("Envio_nao_confirmado_ajuda") }}
          </p>
          <div class="password-result-list">
            <div
              v-for="item in incertos"
              :key="item.chave"
              class="password-result-row password-result-row--warning"
            >
              <v-icon color="warning" small>mdi-help-circle-outline</v-icon>
              <div class="password-result-row__content">
                <strong>{{ item.nome }}</strong>
                <span>{{
                  item.email || $t("Email_destino_nao_informado")
                }}</span>
                <p>{{ item.motivo }}</p>
              </div>
            </div>
          </div>
        </section>

        <v-expansion-panels
          v-if="!processando && sucessos.length"
          flat
          class="password-result-successes mt-5"
        >
          <v-expansion-panel>
            <v-expansion-panel-header>
              {{ $t("Usuarios_com_senha_enviada") }} ({{ sucessos.length }})
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div
                v-for="item in sucessos"
                :key="item.chave"
                class="password-result-success-row"
              >
                <v-icon color="success" small class="mr-2">
                  mdi-check-circle-outline
                </v-icon>
                <span>{{ item.nome }}</span>
                <small v-if="item.email">{{ item.email }}</small>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>

      <v-card-actions class="px-6 pb-5">
        <v-spacer />
        <v-btn
          color="primary"
          class="text-none px-6"
          :disabled="processando"
          @click="$emit('close')"
        >
          {{ $t("Fechar") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "ResultadoEnvioSenhas",
  props: {
    value: { type: Boolean, default: false },
    processando: { type: Boolean, default: false },
    itens: { type: Array, default: () => [] },
  },
  computed: {
    sucessos() {
      return this.itens.filter((item) => item.status === "enviado");
    },
    falhas() {
      return this.itens.filter((item) => item.status === "falha");
    },
    incertos() {
      return this.itens.filter((item) => item.status === "incerto");
    },
    concluidos() {
      return this.sucessos.length + this.falhas.length + this.incertos.length;
    },
    percentualConcluido() {
      return this.itens.length
        ? (this.concluidos / this.itens.length) * 100
        : 0;
    },
  },
};
</script>

<style scoped>
.password-result-title {
  align-items: center;
  word-break: normal;
}

.password-result-progress {
  color: #475569;
  font-size: 0.88rem;
  font-weight: 600;
}

.password-result-stats {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.password-result-stat {
  border: 1px solid;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  padding: 12px 14px;
}

.password-result-stat strong {
  font-size: 1.35rem;
  line-height: 1.2;
}

.password-result-stat span {
  font-size: 0.78rem;
  font-weight: 600;
  margin-top: 4px;
}

.password-result-stat--success {
  background: #effaf3;
  border-color: #c5ead2;
  color: #247045;
}

.password-result-stat--error {
  background: #fff4f2;
  border-color: #f3d0cb;
  color: #a23b32;
}

.password-result-stat--warning {
  background: #fff9ec;
  border-color: #f3e0b3;
  color: #8a621b;
}

.password-result-section {
  margin-top: 22px;
}

.password-result-section h3 {
  color: #263244;
  font-size: 0.96rem;
  margin-bottom: 4px;
}

.password-result-hint {
  color: #64748b;
  font-size: 0.82rem;
  line-height: 1.4;
  margin-bottom: 10px;
}

.password-result-list {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  max-height: 250px;
  overflow-y: auto;
}

.password-result-row {
  display: flex;
  gap: 10px;
  padding: 12px 14px;
}

.password-result-row + .password-result-row {
  border-top: 1px solid #eef0f3;
}

.password-result-row__content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.password-result-row__content strong {
  color: #263244;
  font-size: 0.9rem;
}

.password-result-row__content span {
  color: #64748b;
  font-size: 0.78rem;
  overflow-wrap: anywhere;
}

.password-result-row__content p {
  color: #9b3730;
  font-size: 0.83rem;
  line-height: 1.4;
  margin: 6px 0 0;
  overflow-wrap: anywhere;
}

.password-result-row--warning .password-result-row__content p {
  color: #8a621b;
}

.password-result-successes {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
}

.password-result-success-row {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  padding: 5px 0;
}

.password-result-success-row small {
  color: #64748b;
  margin-left: auto;
  overflow-wrap: anywhere;
}

@media (max-width: 600px) {
  .password-result-stats {
    grid-template-columns: 1fr;
  }
}
</style>
